import { useState } from 'react'
import { ArrowClockwise, CaretDown, CaretRight } from '@phosphor-icons/react'
import { useAuditLog } from '@/hooks/useAuditLog'
import {
  AUDIT_ACTIONS,
  type AuditAction,
  type AuditLogEntry,
} from '@/api/audit'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ---------- Helpers ----------

type DatePreset = 'today' | '7d' | '30d' | 'all'

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: 'all', label: 'All' },
]

function getDateRange(preset: DatePreset): {
  start_date?: string
  end_date?: string
} {
  if (preset === 'all') return {}

  const todayISO = new Date().toISOString().slice(0, 10)

  if (preset === 'today') {
    return { start_date: todayISO, end_date: todayISO }
  }

  const days = preset === '7d' ? 7 : 30
  const startISO = new Date(Date.now() - days * 86400000)
    .toISOString()
    .slice(0, 10)
  return { start_date: startISO, end_date: todayISO }
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const PER_PAGE = 20

// ---------- Inline components ----------

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell className="w-8">
          {expanded ? (
            <CaretDown className="h-4 w-4" />
          ) : (
            <CaretRight className="h-4 w-4" />
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          {formatTimestamp(entry.created_at)}
        </TableCell>
        <TableCell>
          <div>{entry.actor_name}</div>
          <div className="text-xs text-muted-foreground">
            {entry.actor_email}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline">{entry.action}</Badge>
        </TableCell>
        <TableCell className="font-mono text-sm">
          {entry.target_type}/{entry.target_id.slice(0, 8)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={5} className="bg-muted/30 p-4">
            <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
              {JSON.stringify(entry.details, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function TableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Timestamp</TableHead>
          <TableHead>Actor</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 6 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton className="h-4 w-4" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-32" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-28" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-36" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

// ---------- Audit Page ----------

export function Audit() {
  const [page, setPage] = useState(1)
  const [action, setAction] = useState<AuditAction | undefined>(undefined)
  const [datePreset, setDatePreset] = useState<DatePreset>('all')

  const dateRange = getDateRange(datePreset)

  const { data, isLoading, isError, error, isFetching, refetch } =
    useAuditLog({
      action,
      ...dateRange,
      page,
      per_page: PER_PAGE,
    })

  const totalPages = data ? Math.ceil(data.total / PER_PAGE) : 0

  const handleActionChange = (value: string) => {
    setAction(value === '__all__' ? undefined : (value as AuditAction))
    setPage(1)
  }

  const handleDatePresetChange = (preset: DatePreset) => {
    setDatePreset(preset)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Admin action trail across all tenants
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <ArrowClockwise
            className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')}
          />
          Refresh
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Action Filter */}
        <Select
          value={action ?? '__all__'}
          onValueChange={handleActionChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Actions</SelectItem>
            {AUDIT_ACTIONS.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Date Range Preset Buttons */}
        <div className="flex items-center gap-1">
          {DATE_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              variant={datePreset === preset.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDatePresetChange(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Error State */}
      {isError && !data && (
        <Card className="border-destructive">
          <CardContent className="py-6 text-center">
            <p className="text-sm text-destructive font-medium">
              Failed to load audit log
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Audit Table */}
      <Card>
        <CardContent className="pt-0 px-0 pb-0">
          {isLoading ? (
            <TableSkeleton />
          ) : data ? (
            data.items.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No audit entries found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && totalPages > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
            <span className="ml-2">({data.total} entries)</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
